import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">{title}</h2>
    <div className="space-y-4 text-gray-700 dark:text-gray-300">{children}</div>
  </section>
);

const BulletList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc list-outside space-y-2 pl-6 text-gray-700 dark:text-gray-300">
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <main className="max-w-4xl mx-auto">
        <article className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: June 14, 2026</p>

          <div className="max-w-none">
            <p className="text-gray-700 dark:text-gray-300">
              Nexussales.io LLC ("Nexussales.io," "we," "our," or "us") provides customer relationship
              management, workflow automation, messaging, voice, billing, and related business software
              (collectively, the "Services"). This Privacy Policy explains how we collect, use, disclose,
              retain, and protect information when you access or use the Services.
            </p>

            <Section title="1. Information We Collect">
              <p>We collect information you provide, information generated through use of the Services, and information from third parties.</p>
              <BulletList
                items={[
                  'Account information, such as name, email address, phone number, business name, role, address, login information, preferences, and support requests.',
                  'Business verification information, such as company details, EIN or tax information, business documents, A2P brand and campaign details, phone-number order information, and compliance records.',
                  'Customer and lead data that you upload, enter, import, sync, or generate, including names, phone numbers, email addresses, addresses, tags, notes, files, conversations, messages, call logs, recordings if enabled, documents, workflows, campaigns, and analytics.',
                  'Billing information, such as subscription status, plan, invoices, payment method metadata, wallet balances, usage charges, transaction records, refunds or adjustments where applicable, tax information, and payment processor identifiers. Full card details are processed by our payment processor and are not stored by us.',
                  'Usage and device information, such as IP address, browser, operating system, pages viewed, features used, timestamps, logs, cookies, session data, diagnostics, performance data, and security events.',
                  'Information from service providers and integrations, including authentication providers, payment processors, telecom providers, carriers, email providers, analytics services, hosting providers, and connected third-party tools.',
                ]}
              />
            </Section>

            <Section title="2. How We Use Information">
              <BulletList
                items={[
                  'Provide, operate, maintain, secure, support, and improve the Services.',
                  'Create and manage accounts, authenticate users, enforce permissions, and prevent unauthorized access.',
                  'Process payments, subscriptions, wallet funding, phone-number charges, usage billing, invoices, collections, taxes, disputes, and chargebacks.',
                  'Send transactional communications, including account notices, security alerts, billing notices, support responses, service updates, and administrative messages.',
                  'Transmit messages, calls, emails, workflows, campaigns, notifications, and related communications that you initiate or configure through the Services.',
                  'Support A2P, telecom, provider, carrier, fraud, abuse, and compliance reviews.',
                  'Monitor reliability, detect errors, prevent fraud, enforce our Terms, protect rights and safety, and comply with legal obligations.',
                  'Analyze and improve functionality, user experience, performance, pricing, and business operations.',
                  'Send marketing communications about our Services where permitted by law. You may opt out of marketing communications, but we may still send transactional or service-related messages.',
                ]}
              />
            </Section>

            <Section title="3. How We Share Information">
              <p>We do not sell personal information. We may share information as described below:</p>
              <BulletList
                items={[
                  'With service providers that help us host, operate, secure, bill, support, analyze, improve, and deliver the Services.',
                  'With telecom providers, messaging providers, carriers, campaign registries, number providers, and related entities as needed to provide messaging, calling, A2P registration, number ordering, deliverability, compliance, fraud prevention, and support.',
                  'With payment processors, banks, card networks, tax providers, and collections or dispute vendors as needed for billing and payment operations.',
                  'With integrations, apps, or third-party services that you choose to connect or enable.',
                  'With professional advisors, insurers, auditors, lawyers, accountants, and compliance consultants.',
                  'When required by law, subpoena, court order, regulator request, legal process, or to protect rights, safety, security, users, providers, carriers, or the public.',
                  'In connection with a merger, acquisition, financing, reorganization, bankruptcy, sale of assets, or similar business transaction.',
                  'With your direction, consent, or as otherwise disclosed at the time of collection.',
                  'In aggregated, anonymized, or de-identified form that does not reasonably identify you or your contacts.',
                ]}
              />
            </Section>

            <Section title="4. Customer Data and Your Responsibilities">
              <p>
                You control the customer, lead, contact, communication, and business data you submit to the
                Services. You are responsible for providing required notices, obtaining required consents,
                honoring opt-out requests, maintaining accurate records, and ensuring your use of Customer Data
                complies with applicable law, including communications, privacy, consumer protection, and data
                protection laws.
              </p>
            </Section>

            <Section title="5. SMS, Voice, and Communications Data">
              <p>
                When you use messaging or voice features, we and our providers may process sender and recipient
                numbers, message content, call metadata, delivery events, opt-out events, carrier responses,
                campaign information, recordings if enabled, transcripts if enabled, and related logs. This data
                may be shared with telecom providers, carriers, campaign registries, regulators, and other
                parties involved in routing, billing, filtering, fraud prevention, compliance, and support.
              </p>
            </Section>

            <Section title="6. Cookies and Tracking">
              <p>
                We use cookies, local storage, pixels, and similar technologies to authenticate users, maintain
                sessions, remember settings, secure the Services, analyze usage, diagnose issues, and improve
                performance. You can control cookies through browser settings, but disabling cookies may limit
                functionality.
              </p>
            </Section>

            <Section title="7. Data Retention">
              <p>
                We retain information for as long as needed to provide the Services, operate our business,
                comply with legal, tax, accounting, telecom, billing, audit, security, and compliance
                obligations, resolve disputes, enforce agreements, and maintain backups. Retention periods vary
                based on the type of data, account settings, legal requirements, provider requirements, and
                operational needs. We may delete, anonymize, or aggregate information when it is no longer
                needed.
              </p>
            </Section>

            <Section title="8. Security">
              <p>
                We use administrative, technical, and organizational safeguards designed to protect information,
                including access controls, authentication, encryption in transit, provider security controls,
                monitoring, and operational security practices. No system is completely secure, and we cannot
                guarantee absolute security. You are responsible for using strong credentials, limiting user
                access, safeguarding devices, and promptly notifying us of suspected unauthorized access.
              </p>
            </Section>

            <Section title="9. Your Rights and Choices">
              <p>
                Depending on where you live, you may have rights to access, correct, delete, export, restrict,
                or object to certain processing of personal information. You may also opt out of marketing
                emails by using unsubscribe links or contacting us. Some requests may be limited by legal,
                security, billing, fraud-prevention, telecom, backup, or contractual obligations.
              </p>
              <p>
                To submit a privacy request, contact us at{' '}
                <a href="mailto:info@nexussales.io" className="text-blue-600 dark:text-blue-400 hover:underline">
                  info@nexussales.io
                </a>
                .
              </p>
            </Section>

            <Section title="10. California and Other U.S. State Privacy Rights">
              <p>
                Residents of California and other states with privacy laws may have additional rights, including
                the right to know, access, correct, delete, obtain a copy of, or opt out of certain uses of
                personal information. We do not sell personal information. If our practices are considered
                "sharing" or targeted advertising under applicable law, you may contact us to exercise available
                opt-out rights.
              </p>
            </Section>

            <Section title="11. International Transfers">
              <p>
                We are based in the United States, and information may be processed in the United States and in
                other countries where we or our service providers operate. These countries may have data
                protection laws that differ from those in your location.
              </p>
            </Section>

            <Section title="12. Children's Privacy">
              <p>
                The Services are intended for business users and are not directed to children under 18. We do
                not knowingly collect personal information from children under 18. If you believe a child has
                provided information to us, contact us and we will take appropriate steps.
              </p>
            </Section>

            <Section title="13. Third-Party Links and Services">
              <p>
                The Services may link to or integrate with third-party websites, platforms, providers, or
                applications. Their privacy practices are governed by their own policies. We are not responsible
                for third-party privacy, security, or data practices.
              </p>
            </Section>

            <Section title="14. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Updated versions will be posted on our
                website or made available through the Services. Your continued use of the Services after an
                update means you accept the updated Privacy Policy.
              </p>
            </Section>

            <Section title="15. Contact Us">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <p>If you have questions, concerns, or requests about privacy or data practices, contact:</p>
                <p>
                  <strong>Nexussales.io LLC</strong>
                  <br />
                  Email:{' '}
                  <a href="mailto:info@nexussales.io" className="text-blue-600 dark:text-blue-400 hover:underline">
                    info@nexussales.io
                  </a>
                  <br />
                  Website:{' '}
                  <a
                    href="https://crm.nexussales.io"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    crm.nexussales.io
                  </a>
                </p>
              </div>
            </Section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default Privacy;
